import React from "react";
import Select, { createFilter } from "react-select";

const resellerFilter = createFilter({ stringify: (o) => o.label });

const pickerStyles = {
    menu: (base) => ({ ...base, zIndex: 5 }),
};

function idOf(r) {
    return String(r.resellerID || r.resellerId);
}

/**
 * Task 37 N13 — the master administrator's reseller picker, shared by the setup and status screens.
 *
 * Searchable, because it lists every reseller on the platform. It starts EMPTY — "Select a reseller"
 * — rather than on the master account's own reseller: opening the screen used to show reseller 1's
 * own billing as though that were the obvious subject, when a master administrator is almost always
 * here to look at somebody else's. Their own stays one choice away as "My own reseller".
 *
 * Presentational only. Whether it is offered at all is the server's decision, made by the page.
 *
 * @param resellers   The master-admin reseller list, as MasterAdmin/GetResellers returns it.
 * @param ownId       The master administrator's own reseller id, offered as "My own reseller".
 * @param value       The chosen reseller id, or null while nothing has been chosen.
 * @param onChange    Called with the chosen id as a string.
 * @param hint        One line under the picker saying what may be done with the choice.
 * @param inputId     For the label.
 */

export default function ResellerPicker({ resellers, ownId, value, onChange, hint, inputId }) {
    const own = { value: String(ownId), label: "My own reseller" };
    const options = [own].concat(
        (resellers || [])
            // The master account's own entry, if the list carries one, is already "My own reseller".
            .filter((r) => idOf(r) !== own.value)
            .map((r) => ({
                value: idOf(r),
                label: r.resellerName || r.name || `Reseller ${idOf(r)}`,
            }))
    );

    const selected = value == null ? null : options.find((o) => o.value === String(value)) || null;

    return (
        <div className="mb-3" style={{ maxWidth: 420 }}>
            <label className="form-label fw-semibold" htmlFor={inputId}>
                Reseller
            </label>
            <Select
                inputId={inputId}
                options={options}
                value={selected}
                isSearchable
                placeholder="Select a reseller"
                filterOption={resellerFilter}
                noOptionsMessage={() => "No resellers match that search"}
                styles={pickerStyles}
                onChange={(o) => onChange(o ? o.value : null)}
            />
            {hint && (
                <div className="text-muted" style={{ fontSize: 13 }}>
                    {hint}
                </div>
            )}
        </div>
    );
}

/**
 * Said in place of the picker when the session belongs to the master account but the server would
 * not confirm it. Hiding the control without a word is how N13 went unnoticed: the screen simply
 * looked as though master administrators had never been given a picker.
 */
export function ResellerPickerUnavailable() {
    return (
        <div className="text-muted mb-3" style={{ fontSize: 13 }}>
            Choosing another reseller is not available for this account. Contact support if you need it.
        </div>
    );
}

/** Shown instead of the screen's content until a reseller has been chosen. */
export function ChooseResellerPrompt({ detail }) {
    return (
        <div className="alert alert-light border text-center py-5 mb-0">
            <p className="mb-1 fw-semibold">Choose a reseller</p>
            <p className="mb-0 text-muted" style={{ fontSize: 14 }}>
                {detail}
            </p>
        </div>
    );
}
